import { useCallback, useState } from "react";
import { useIconPop } from "@client/hooks/useIconPop";
import { createExternalStore } from "@client/utils/createExternalStore";

// Cross-component pulse for the top-bar Queue icon (the ListVideoIcon in
// SiteHeader's QueueDrawer trigger). A Save / Up Next toggle on a video card
// lives deep in the funder workspace, far from the header, so there's no shared
// context to bridge them — a module-level counter carries the "the user just
// added something to their queue" signal up to the icon, which reads it via
// useSyncExternalStore and replays the same `save-pop` glyph animation the card
// icons use.
//
// Like useIconPop (which drives it), this is fired from the TAP, never from
// observing playlist/bookmark state: that state flips false→true when the
// video-state store hydrates from the server on load, which would bounce the
// header icon on every page load. The counter only increments when a user
// activates a toggle, so hydration can't trigger it.
const store = createExternalStore<number>(0);

// Called from a Save / Up Next toggle handler when it activates. Bumps the
// counter so subscribers replay their pop animation.
export function pulseQueueIcon(): void {
  store.set(store.get() + 1);
}

// useIconPop for controls whose action lands an item in the Queue drawer —
// Save and Up Next on a video/clip. Identical pop feedback to useIconPop, and
// it additionally pulses the top-bar Queue icon so the header reacts in
// sympathy with the tapped card. Other useIconPop callers (copy-link, etc.)
// deliberately stay on the plain hook: their action doesn't touch the queue, so
// the header must not move. Call `pop()` only when the toggle activates (a
// real add), never on load — same rule as useIconPop, for the same
// hydration-bounce reason.
export function useQueueItemPop(): { popKey: number; pop: () => void } {
  const { popKey, pop } = useIconPop();
  const pulsingPop = useCallback(() => {
    pop();
    pulseQueueIcon();
  }, [pop]);
  return { popKey, pop: pulsingPop };
}

// Pulses observed since THIS mount — 0 on mount, +1 per Save/Up Next tap while
// mounted. Spread as BOTH the header glyph's React `key` and its `save-pop` /
// ping gate (`pulseKey > 0`), mirroring useIconPop's popKey usage: the key
// remounts the glyph so the finite CSS animation replays on every pulse, and the
// `> 0` gate keeps the first (mount) render static.
//
// The subtraction is load-bearing: the store is module-level and OUTLIVES this
// component, but SiteHeader unmounts QueueDrawer whenever the round context
// clears (leaving / switching rounds). Returning the raw counter would carry an
// earlier round's count into the remount, so the icon would ping with no tap —
// the very load-bounce this feature exists to avoid. Baselining to the
// mount-time value restores useIconPop's per-instance "starts at 0 every mount"
// semantics that a module store doesn't get for free. The counter only ever
// increments, so the result is always >= 0.
export function useQueueIconPulse(): number {
  const raw = store.useValue();
  const [baseline] = useState(raw);
  return raw - baseline;
}
